import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useStreamers } from '../context/StreamersContext';
import { resolveNavbarMenuItems } from '../utils/navbarMenu';
import '../styles/Navbar.css';

const Navbar: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
    const [isAdmin, setIsAdmin] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t, locale, setLocale } = useLanguage();
    const { config } = useSiteConfig();
    const { onlineCount } = useStreamers();
    const discordUrl = config.links?.discord || 'https://discord.gg/ps9smnSu9j';
    const isHome = location.pathname === '/';
    const menuItems = resolveNavbarMenuItems(config.navbar?.menuItems);
    const logoSrc = config.navbar?.logo?.src || '/assets/images/logo.png';
    const logoAlt = config.navbar?.logo?.alt || (config.general?.siteName || 'Knight Icarus Online');
    const logoHeight = config.navbar?.logo?.heightPx || 40;
    const logoPosition = config.navbar?.logoPosition || 'left';

    const handleLogout = () => {
        authService.logout();
        setIsAuthenticated(false);
        setIsAdmin(false);
        navigate('/');
    };

    const user = authService.getCurrentUser();

    useEffect(() => {
        const authed = authService.isAuthenticated();
        setIsAuthenticated(authed);
        if (!authed) {
            setIsAdmin(false);
            return;
        }
        const cached = sessionStorage.getItem('isAdminVerified');
        if (cached === '1') setIsAdmin(true);
        else if (cached === '0') setIsAdmin(false);

        let cancelled = false;
        authService.checkIsAdmin().then((admin) => {
            if (!cancelled) setIsAdmin(admin);
        });
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    useEffect(() => {
        setIsAuthenticated(authService.isAuthenticated());
    }, [location.pathname]);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 48);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const navClass = [
        'navbar',
        isHome && !scrolled ? 'navbar--hero' : '',
        scrolled ? 'scrolled' : '',
    ].filter(Boolean).join(' ');

    const renderMenuLinks = () => (
        (menuItems && menuItems.length > 0) ? (
            menuItems.map(item => {
                const label = item.label?.[locale] || item.label?.en || item.label?.['pt-BR'] || item.id;
                if (item.type === 'internal') {
                    return (
                        <div className="nav-item" key={item.id}>
                            <Link
                                to={item.href}
                                onClick={() => setIsMenuOpen(false)}
                                className={item.id === 'live' && onlineCount > 0 ? 'live-online' : undefined}
                            >
                                {label}
                            </Link>
                        </div>
                    );
                }
                const href = item.id === 'discord' ? (config.links?.discord || item.href || discordUrl) : item.href;
                const newTab = item.newTab !== false;
                return (
                    <div className="nav-item" key={item.id}>
                        <a
                            href={href}
                            target={newTab ? '_blank' : undefined}
                            rel={newTab ? 'noopener noreferrer' : undefined}
                        >
                            {label}
                        </a>
                    </div>
                );
            })
        ) : (
            <>
                <div className="nav-item">
                    <Link to="/" onClick={() => setIsMenuOpen(false)}>{t('nav.home')}</Link>
                </div>
                <div className="nav-item">
                    <Link to="/game" onClick={() => setIsMenuOpen(false)}>{t('nav.game')}</Link>
                </div>
                <div className="nav-item">
                    <Link to="/classes" onClick={() => setIsMenuOpen(false)}>{t('nav.classes')}</Link>
                </div>
                <div className="nav-item">
                    <Link to="/news" onClick={() => setIsMenuOpen(false)}>{t('nav.news')}</Link>
                </div>
                <div className="nav-item">
                    <a href={discordUrl} target="_blank" rel="noopener noreferrer">{t('nav.community')}</a>
                </div>
            </>
        )
    );

    return (
        <nav className={navClass}>
            <div className="container navbar-inner">
                <Link to="/" className={`logo ${logoPosition === 'center' ? 'logo-center' : ''}`}>
                    <img
                        src={logoSrc}
                        alt={logoAlt}
                        style={{ height: `${logoHeight}px`, width: 'auto' }}
                    />
                </Link>

                <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
                    {renderMenuLinks()}
                    {isAuthenticated && isAdmin && (
                        <div className="nav-item">
                            <Link to="/admin" onClick={() => setIsMenuOpen(false)}>{t('nav.admin')}</Link>
                        </div>
                    )}
                    <div className="nav-mobile-actions">
                        <div className="lang-switcher">
                            <button
                                type="button"
                                className={`lang-btn ${locale === 'en' ? 'active' : ''}`}
                                onClick={() => setLocale('en')}
                            >
                                EN
                            </button>
                            <button
                                type="button"
                                className={`lang-btn ${locale === 'pt-BR' ? 'active' : ''}`}
                                onClick={() => setLocale('pt-BR')}
                            >
                                PT
                            </button>
                        </div>
                        {isAuthenticated && isAdmin && (
                            <Link to="/admin" className="btn btn-secondary" onClick={() => setIsMenuOpen(false)}>
                                {t('nav.admin')}
                            </Link>
                        )}
                        {isAuthenticated ? (
                            <>
                                <Link to="/usercp" className="btn btn-secondary" onClick={() => setIsMenuOpen(false)}>
                                    {t('nav.myAccount')}
                                </Link>
                                <button type="button" onClick={handleLogout} className="btn btn-secondary">
                                    {t('nav.logout')}
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="btn btn-secondary" onClick={() => setIsMenuOpen(false)}>
                                    {t('nav.login')}
                                </Link>
                                <Link to="/register" className="btn btn-primary" onClick={() => setIsMenuOpen(false)}>
                                    {t('nav.register')}
                                </Link>
                            </>
                        )}
                        <Link to="/download" className="btn btn-primary nav-play-btn" onClick={() => setIsMenuOpen(false)}>
                            {t('nav.playNow')}
                        </Link>
                    </div>
                </div>

                <div className="nav-actions nav-actions--desktop">
                    <div className="lang-switcher">
                        <button
                            type="button"
                            className={`lang-btn ${locale === 'en' ? 'active' : ''}`}
                            onClick={() => setLocale('en')}
                            title="English"
                        >
                            EN
                        </button>
                        <button
                            type="button"
                            className={`lang-btn ${locale === 'pt-BR' ? 'active' : ''}`}
                            onClick={() => setLocale('pt-BR')}
                            title="Português"
                        >
                            PT
                        </button>
                    </div>
                    {isAuthenticated ? (
                        <>
                            <span className="username">{user?.account}</span>
                            {isAdmin && (
                                <Link to="/admin" className="btn btn-secondary btn-compact">
                                    {t('nav.admin')}
                                </Link>
                            )}
                            <Link to="/usercp" className="btn btn-secondary btn-compact">
                                {t('nav.myAccount')}
                            </Link>
                            <button type="button" onClick={handleLogout} className="btn btn-secondary btn-compact">
                                {t('nav.logout')}
                            </button>
                        </>
                    ) : (
                        <Link to="/login" className="btn btn-secondary btn-compact">
                            {t('nav.login')}
                        </Link>
                    )}
                    <Link to="/download" className="btn btn-primary nav-play-btn">
                        {t('nav.playNow')}
                    </Link>
                </div>

                <button
                    type="button"
                    className="mobile-menu-btn"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-expanded={isMenuOpen}
                    aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                >
                    <i className={isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} />
                </button>
            </div>
        </nav>
    );
};

export default Navbar;
